import { useMemo, useState } from "react";
import { Row, Col, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useDSPAudienceDatasetsQuery } from "../hooks/useDSPAudienceDatasetsQuery";

const AudienceExpander = ({ row, lineItemId }) => {
  const { t } = useTranslation(["dsp"]);
  const [tab, setTab] = useState("overview");
  const { group } = useDates();

  const { segment_id: segmentId, segment_name: name } = row.original ?? {};
  const hasImpression = row.original?.impressions;

  const { data: audienceResult, isLoading: isAudienceDataLoading } = useDSPAudienceDatasetsQuery({
    dimensions: ["segment_id"],
    segmentId,
    lineItemId,
  });

  const { data: audienceTimeSeriesResult, isLoading: isAudienceTimeSeriesDataLoading } = useDSPAudienceDatasetsQuery({
    dimensions: ["segment_id", group],
    segmentId,
    lineItemId,
  });

  const data = useMemo(() => {
    const timeSeriesData = audienceTimeSeriesResult?.data ?? [];
    return {
      date: timeSeriesData.map((row) => ({ ...row, start_date: row.start_date ?? row.date })),
      total: audienceResult?.data?.[0] ?? {},
      metadata: audienceResult?.data?.[0] ?? {},
      isLoading: isAudienceDataLoading,
    };
  }, [audienceResult, audienceTimeSeriesResult, isAudienceDataLoading]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["dsp_sales", "dsp_cost", "dsp_units"],
    "dspLineItemExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("dsp_sales"),
      getMetricSpec("dsp_total_sales"),
      getMetricSpec("dsp_impressions"),
      getMetricSpec("dsp_clicks"),
      getMetricSpec("dsp_cost"),
      getMetricSpec("dsp_total_purchases"),
    ],
    [],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isAudienceDataLoading || isAudienceTimeSeriesDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {name}
          </h3>
        </Col>
      </Row>
      <Row className="px-3">
        <Col xs={12} className="fs-4 my-3 border-bottom">
          <Nav
            variant="underline"
            onSelect={(key) => {
              return setTab(key);
            }}
            activeKey={tab}
          >
            <Nav.Item>
              <Nav.Link eventKey="overview">{t(($) => $.dsp["Overview"])}</Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>
      <Row>
        {tab === "overview" && (
          <>
            {hasImpression ?
              <>
                <Col xs={12}>
                  <MultiMetricChart
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    data={data?.date ?? []}
                    group={group}
                    handleLegendChange={handleLegendChange}
                    showAnnotations={false}
                  />
                </Col>
                <Col xs={12}>
                  <Row className="pt-4">
                    {metrics?.map((m, i) => {
                      return (
                        <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric center metric={m} data={data?.total} showTarget={false} />
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              </>
            : <Col xs={12} className="text-center pb-5">
                <h4>{t(($) => $.dsp["No Data Available"])}</h4>
                <p>{t(($) => $.dsp["This section will be populated once the audience has at least 1 impression."])}</p>
              </Col>
            }
          </>
        )}
      </Row>
    </div>
  );
};

export default AudienceExpander;
